/**
 * DataLineageModal Component
 *
 * Modal dialog that displays the data lineage (provenance) of a railML file
 * Shows the high-level dataset chain from source systems to the final railML file
 */

import { Dialog, Link, LoadingState, Text } from '@bn-apps/dim-ui-react';
import { useTranslation } from 'react-i18next';
import routes from '@config/routes';
import type { DataLineage } from '@/types';
import { getIntlLocale } from '@utils/locale.utils';
import styles from './DataLineageModal.module.scss';

interface DataLineageModalProps {
  isOpen: boolean;
  onClose: () => void;
  lineage: DataLineage | null;
  loading: boolean;
  fileName: string;
}

function DataLineageModal({ isOpen, onClose, lineage, loading, fileName }: DataLineageModalProps): JSX.Element {
  const { t, i18n } = useTranslation();

  const formatTimestamp = (date: Date): string => {
    return new Intl.DateTimeFormat(getIntlLocale(i18n.language), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).format(date);
  };

  return (
    <Dialog
      isOpen={isOpen}
      onOpenChange={(open) => { if (!open) onClose(); }}
      title={t('files.dataLineage.title')}
    >
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <Text className={styles.description}>
            {t('files.dataLineage.description', { fileName })}
          </Text>
          <Link href={routes.helpDataLineage} onClick={onClose} className={styles.learnMoreLink}>
            {t('files.dataLineage.helpLink')} →
          </Link>
        </div>

        {loading && (
          <LoadingState />
        )}

        {!loading && !lineage && (
          <Text tone="secondary">
            {t('files.dataLineage.noData')}
          </Text>
        )}

        {!loading && lineage && (
          <div className={styles.lineageChain}>
            {/* railML File */}
            <div className={`${styles.lineageItem} ${styles.railmlFile}`}>
              <div className={styles.lineageIcon}>📄</div>
              <div className={styles.lineageContent}>
                <div className={styles.lineageHeader}>
                  <Text>
                    {t('files.dataLineage.railmlFile')}
                  </Text>
                </div>
                <dl className={styles.lineageDetails}>
                  <dt><Text>{t('files.dataLineage.detailTitle')}:</Text></dt>
                  <Text>{lineage.railmlFile.title}</Text>
                  <dt><Text>{t('files.dataLineage.creationTime')}:</Text></dt>
                  <dd><Text>{formatTimestamp(lineage.railmlFile.created)}</Text></dd>
                  {lineage.railmlFile.identifier && (
                    <>
                      <dt><Text>{t('files.dataLineage.identifier')}:</Text></dt>
                      <dd className={styles.identifier}><Text>{lineage.railmlFile.identifier}</Text></dd>
                    </>
                  )}
                </dl>
              </div>
            </div>

            {lineage.sourceTtl && (
              <>
                <div className={styles.lineageArrow}>
                  <div className={styles.arrowLine}></div>
                  <span className={styles.arrowIcon}>↑</span>
                </div>

                {/* Source TTL */}
                <div className={`${styles.lineageItem} ${styles.sourceTtl}`}>
                  <div className={styles.lineageIcon}>📋</div>
                  <div className={styles.lineageContent}>
                    <div className={styles.lineageHeader}>
                      <Text>
                        {t('files.dataLineage.sourceTtl')}
                      </Text>
                    </div>
                    <Text weight="bold" className={styles.itemTitle}>
                      {lineage.sourceTtl.title}
                    </Text>
                    <dl className={styles.lineageDetails}>
                      <dt><Text>{t('files.dataLineage.creationTime')}:</Text></dt>
                      <dd><Text>{formatTimestamp(lineage.sourceTtl.created)}</Text></dd>
                      {lineage.sourceTtl.identifier && (
                        <>
                          <dt><Text>{t('files.dataLineage.identifier')}:</Text></dt>
                          <dd className={styles.identifier}><Text>{lineage.sourceTtl.identifier}</Text></dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              </>
            )}

            {lineage.knowledgeGraph && (
              <>
                <div className={styles.lineageArrow}>
                  <div className={styles.arrowLine}></div>
                  <span className={styles.arrowIcon}>↑</span>
                </div>

                {/* Knowledge Graph */}
                <div className={`${styles.lineageItem} ${styles.knowledgeGraph}`}>
                  <div className={styles.lineageIcon}>🗂️</div>
                  <div className={styles.lineageContent}>
                    <div className={styles.lineageHeader}>
                      <Text weight="semibold" tone="secondary">
                        {t('files.dataLineage.knowledgeGraph')}
                      </Text>
                    </div>
                    <Text weight="bold" className={styles.itemTitle}>
                      {lineage.knowledgeGraph.title}
                    </Text>
                    <dl className={styles.lineageDetails}>
                      <dt><Text>{t('files.dataLineage.creationTime')}:</Text></dt>
                      <dd><Text>{formatTimestamp(lineage.knowledgeGraph.created)}</Text></dd>
                      {lineage.knowledgeGraph.identifier && (
                        <>
                          <dt><Text>{t('files.dataLineage.identifier')}:</Text></dt>
                          <dd className={styles.identifier}><Text>{lineage.knowledgeGraph.identifier}</Text></dd>
                        </>
                      )}
                    </dl>
                  </div>
                </div>
              </>
            )}

            {lineage.sources.length > 0 && (
              <>
                <div className={styles.lineageArrow}>
                  <div className={styles.arrowLine}></div>
                  <span className={styles.arrowIcon}>↑</span>
                </div>

                {/* Source Systems */}
                <div className={`${styles.lineageItem} ${styles.sourceSystems}`}>
                  <div className={styles.lineageIcon}>💾</div>
                  <div className={styles.lineageContent}>
                    <div className={styles.lineageHeader}>
                      <Text weight="semibold" tone="secondary">
                        {t('files.dataLineage.sourceSystems')}
                      </Text>
                      <Text variant="table" tone="secondary">
                        {lineage.sources.length} {lineage.sources.length === 1 ? t('files.dataLineage.source') : t('files.dataLineage.sources')}
                      </Text>
                    </div>
                    <div className={styles.sourcesGrid}>
                      {lineage.sources.map((source, index) => (
                        <div key={index} className={styles.sourceCard}>
                          <Text weight="semibold" className={styles.sourceName}>
                            {source.title}
                          </Text>
                          <Text variant="table" tone="secondary">
                            {formatTimestamp(source.created)}
                          </Text>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className={styles.footnote}>
          <Text tone="secondary">
            {t('files.dataLineage.note')}
          </Text>
        </div>
      </div>
    </Dialog>
  );
}

export default DataLineageModal;
